import React, { useEffect, useRef, useState } from "react";

export default function ProductReveal({
  children,
  direction = "down",
  index = 0,
  className = "",
}) {
  const itemRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const handleMotionPreference = () => {
      setReduceMotion(mediaQuery.matches);
    };

    handleMotionPreference();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleMotionPreference);
    } else {
      mediaQuery.addListener(handleMotionPreference);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleMotionPreference);
      } else {
        mediaQuery.removeListener(handleMotionPreference);
      }
    };
  }, []);

  useEffect(() => {
    const element = itemRef.current;

    if (!element) return;

    if (reduceMotion) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.14,
        rootMargin: "0px 0px -8% 0px",
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [reduceMotion]);

  const hiddenX = direction === "up" ? 36 : -36;
  const delay = Math.min(index * 35, 180);

  const revealStyle = reduceMotion
    ? undefined
    : {
        opacity: isVisible ? 1 : 0,
        transform: isVisible
          ? "translate3d(0, 0, 0)"
          : `translate3d(${hiddenX}px, 10px, 0)`,
        filter: isVisible ? "blur(0px)" : "blur(2px)",
        transition: [
          `opacity 420ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
          `transform 420ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
          `filter 420ms cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
        ].join(", "),
        willChange: "opacity, transform, filter",
      };

  return (
    <div
      ref={itemRef}
      className={["w-full", className].filter(Boolean).join(" ")}
      style={revealStyle}
    >
      {children}
    </div>
  );
}